import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import BookingPartners from "./BookingPartners";
import logo from "../assets/logo.png";
import { supabase } from "../supabaseClient";
import { useAuthModal } from "../AuthModalContext";

const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.35)",
  accentBg: "rgba(12,105,200,0.10)",
  accentBorder: "rgba(12,105,200,0.28)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.07)",
  borderMd: "rgba(255,255,255,0.12)",
  text: "#FAFAFA",
  textSec: "rgba(250,250,250,0.65)",
  textMuted: "rgba(250,250,250,0.36)",
  shadowLg: "0 16px 56px rgba(0,0,0,0.75)",
  headerBg: "rgba(10,10,10,0.93)",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.22)",
  accentBg: "rgba(12,105,200,0.06)",
  accentBorder: "rgba(12,105,200,0.20)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgElevated: "#EDF0F5",
  border: "rgba(10,29,68,0.08)",
  borderMd: "rgba(10,29,68,0.13)",
  text: "#0F1629",
  textSec: "#3D4A6B",
  textMuted: "#7A87A8",
  shadowLg: "0 16px 56px rgba(10,29,68,0.12)",
  headerBg: "rgba(245,247,250,0.94)",
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem("spa-theme");
      if (saved) return saved !== "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return true;
    }
  });
  const toggle = useCallback(() => {
    setDark((value) => {
      const next = !value;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  return [dark ? DARK : LIGHT, toggle, dark];
}

const plans = [
  {
    id: "commission",
    eyebrow: "Recommended",
    title: "Commission Model",
    price: "Free to list",
    bullets: [
      "We take 15% per booking made through Snows ProAm",
      "No monthly fee",
      "Perfect for getting started",
    ],
    cta: "List For Free",
    featured: true,
  },
  {
    id: "pro_partner",
    eyebrow: "Monthly plan",
    title: "Pro Partner",
    price: "€99/month",
    bullets: [
      "No commission on bookings",
      "Featured placement on map and directory",
      "Priority support",
      "Analytics dashboard",
    ],
    cta: "Start Pro Partner",
  },
];

export default function VenueJoinFlow() {
  const [C, toggleTheme, isDark] = useTheme();
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const [selected, setSelected] = useState(() => localStorage.getItem("venue_pricing_model") || "");
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [linkedWorkspaceId, setLinkedWorkspaceId] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const signInPromptedRef = useRef(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
      html{scroll-behavior:smooth}
      body{font-family:'DM Sans',sans-serif}
      @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
      .fu{animation:fadeUp .55s cubic-bezier(.16,1,.3,1) both}
      @media(max-width:760px){.venue-plan-grid{grid-template-columns:1fr!important}.hide-mobile{display:none!important}}
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const loadBusinessAccess = useCallback(async () => {
    setAuthLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const nextUser = sessionData?.session?.user || null;
      setUser(nextUser);
      if (!nextUser?.id) {
        setProfile(null);
        setLinkedWorkspaceId(null);
        return;
      }
      const [{ data: profileData }, { data: membershipRows }] = await Promise.all([
        supabase.from("profiles").select("id,name,email,role,account_type,venue_model").eq("id", nextUser.id).maybeSingle(),
        supabase.from("workspace_members").select("workspace_id,workspace_kind,role").eq("profile_id", nextUser.id).limit(20),
      ]);
      setProfile(profileData || null);
      setBusinessName((profileData?.name || nextUser.user_metadata?.display_name || "").trim());
      const businessMembership = (membershipRows || []).find((row) => ["business", "venue"].includes(row.workspace_kind));
      setLinkedWorkspaceId(businessMembership?.workspace_id || null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinessAccess();
    const { data } = supabase.auth.onAuthStateChange(() => {
      signInPromptedRef.current = false;
      loadBusinessAccess();
    });
    return () => data?.subscription?.unsubscribe?.();
  }, [loadBusinessAccess]);

  useEffect(() => {
    if (!selected || authLoading || user || signInPromptedRef.current) return;
    signInPromptedRef.current = true;
    openAuthModal({
      mode: "signin",
      title: "Sign in to list a venue",
      body: "Use your Snows ProAm profile first. We will link venue management to that profile after sign in.",
      redirectTo: "/venues/join",
    });
  }, [authLoading, openAuthModal, selected, user]);

  const isBusinessLinked = !!linkedWorkspaceId || profile?.role === "partner" || profile?.account_type === "business" || profile?.account_type === "institute" || !!profile?.venue_model;

  const linkBusinessAccount = async (event) => {
    event.preventDefault();
    const name = businessName.trim();
    if (!name) {
      setLinkError("Enter the business or venue account name.");
      return;
    }
    setLinking(true);
    setLinkError("");
    try {
      const { data, error } = await supabase.rpc("ensure_business_workspace", {
        p_name: name,
        p_kind: "venue",
        p_venue_model: selected || localStorage.getItem("venue_pricing_model") || null,
      });
      if (error) throw error;
      setLinkedWorkspaceId(data || "linked");
      await loadBusinessAccess();
    } catch (err) {
      console.warn("[VenueJoinFlow] business link failed", err);
      setLinkError("We could not link your business account right now. Please check the name and try again.");
    } finally {
      setLinking(false);
    }
  };

  if (selected) {
    if (authLoading) {
      return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: C.bg, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>Loading venue access...</div>;
    }
    if (!user) {
      return (
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 20, background: C.bg, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>
          <div style={{ maxWidth: 420, width: "100%", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24, textAlign: "center", boxShadow: C.shadowLg }}>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, margin: "0 0 8px" }}>Sign in to list a venue</h1>
            <p style={{ color: C.textSec, lineHeight: 1.55, margin: "0 0 18px" }}>Create or use your Snows ProAm profile first, then link venue management to it.</p>
            <button type="button" onClick={() => openAuthModal({ mode: "signin", title: "Sign in to list a venue", body: "Use your Snows ProAm profile first. We will link venue management to that profile after sign in.", redirectTo: "/venues/join" })} style={{ width: "100%", border: "none", borderRadius: 12, padding: "13px 14px", background: C.accentGrad, color: "#fff", fontFamily: "'Sora',sans-serif", fontWeight: 900, cursor: "pointer" }}>Sign In</button>
          </div>
        </div>
      );
    }
    if (!isBusinessLinked) {
      return (
        <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 20, background: C.bg, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>
          <form onSubmit={linkBusinessAccount} style={{ maxWidth: 460, width: "100%", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24, boxShadow: C.shadowLg }}>
            <div style={{ display: "inline-flex", padding: "5px 10px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .5, marginBottom: 14 }}>Business link</div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, margin: "0 0 8px" }}>Link this profile to venue management.</h1>
            <p style={{ color: C.textSec, lineHeight: 1.55, margin: "0 0 18px" }}>You will still use your normal Snows ProAm profile. This adds a business workspace for venues, bookings, and dashboard tools.</p>
            <label style={{ display: "grid", gap: 7, color: C.textSec, fontSize: 12, fontWeight: 900, marginBottom: 12 }}>
              Business or venue account name
              <input value={businessName} onChange={(event) => setBusinessName(event.target.value)} autoComplete="organization" style={{ width: "100%", borderRadius: 10, border: `1px solid ${C.borderMd}`, background: C.bgElevated, color: C.text, padding: "12px 13px", fontSize: 14, fontFamily: "'DM Sans',sans-serif" }} />
            </label>
            {linkError ? <div role="alert" style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(239,68,68,.10)", border: "1px solid rgba(239,68,68,.24)", color: "#EF4444", fontSize: 13, fontWeight: 800, marginBottom: 12 }}>{linkError}</div> : null}
            <button type="submit" disabled={linking} style={{ width: "100%", border: "none", borderRadius: 12, padding: "13px 14px", background: C.accentGrad, color: "#fff", fontFamily: "'Sora',sans-serif", fontWeight: 900, cursor: linking ? "wait" : "pointer", opacity: linking ? .72 : 1 }}>{linking ? "Linking..." : "Link Business Account"}</button>
          </form>
        </div>
      );
    }
    return <BookingPartners />;
  }

  const choosePlan = (planId) => {
    localStorage.setItem("venue_pricing_model", planId);
    setSelected(planId);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>
      <header style={{ position: "sticky", top: 0, zIndex: 20, height: 56, display: "flex", alignItems: "center", padding: "0 16px", background: C.headerBg, backdropFilter: "blur(20px) saturate(1.2)", WebkitBackdropFilter: "blur(20px) saturate(1.2)", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
            </div>
            <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, color: C.text }}>Snows ProAm <span style={{ color: C.accent }}>Venues</span></span>
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, fontSize: 10, fontWeight: 800, color: C.accent, letterSpacing: 0.4 }}>
            JOIN
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={toggleTheme} title={isDark ? "Light mode" : "Dark mode"} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, color: C.text, cursor: "pointer" }}>
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} />
            </button>
            <button onClick={() => navigate(-1)} style={{ fontSize: 12, fontWeight: 500, color: C.textSec, padding: "5px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
              ← Back
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1080, margin: "0 auto", padding: "76px 18px 90px" }}>
        <div className="fu" style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 34px" }}>
          <div style={{ display: "inline-flex", padding: "5px 10px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: .5, marginBottom: 14 }}>Add Your Venue</div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(36px,7vw,68px)", lineHeight: .95, letterSpacing: -1, margin: "0 0 16px" }}>
            Pick how your venue grows on Snows ProAm.
          </h1>
          <p style={{ color: C.textSec, fontSize: 17, lineHeight: 1.65, margin: 0 }}>
            Choose a model first. Then create your venue profile, add facilities, and start taking bookings.
          </p>
        </div>

        <div className="venue-plan-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          {plans.map((plan, index) => (
            <div key={plan.id} className="fu" style={{ animationDelay: `${index * .08}s`, position: "relative", background: C.bgCard, border: `1.5px solid ${plan.featured ? C.accent : C.borderMd}`, borderRadius: 18, padding: 26, boxShadow: plan.featured ? `0 0 0 1px ${C.accentBorder}, 0 18px 60px ${C.accentGlow}` : C.shadowLg }}>
              <div style={{ display: "inline-flex", padding: "5px 10px", borderRadius: 999, background: plan.featured ? C.accentGrad : C.bgElevated, color: plan.featured ? "#fff" : C.textSec, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: .5, marginBottom: 18 }}>{plan.eyebrow}</div>
              <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, margin: "0 0 8px" }}>{plan.title}</h2>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 34, fontWeight: 900, color: plan.featured ? C.accent : C.text, marginBottom: 18 }}>{plan.price}</div>
              <div style={{ display: "grid", gap: 12, marginBottom: 26 }}>
                {plan.bullets.map((bullet) => (
                  <div key={bullet} style={{ display: "flex", gap: 10, color: C.textSec, fontWeight: 600 }}>
                    <span style={{ color: C.accent, fontWeight: 900 }}>✓</span>
                    {bullet}
                  </div>
                ))}
              </div>
              <button onClick={() => choosePlan(plan.id)} style={{ width: "100%", border: "none", borderRadius: 12, padding: "14px 18px", cursor: "pointer", background: plan.featured ? C.accentGrad : C.bgElevated, color: plan.featured ? "#fff" : C.text, fontFamily: "'Sora',sans-serif", fontWeight: 800, boxShadow: plan.featured ? `0 8px 28px ${C.accentGlow}` : "none" }}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
